import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ownerPortal } from '../services/api';
import toast from 'react-hot-toast';

const STATUS_COLORS = {
  submitted: 'bg-yellow-100 text-yellow-800',
  acknowledged: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-purple-100 text-purple-800',
  completed: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-600',
};

const FEE_STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800',
  overdue: 'bg-red-100 text-red-800',
  paid: 'bg-green-100 text-green-800',
};

export default function OwnerDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    ownerPortal.dashboard()
      .then((res) => setData(res.data))
      .catch(() => toast.error('Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
    </div>
  );

  if (!data?.unit) return (
    <div className="max-w-lg mx-auto mt-16 text-center">
      <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-10 h-10 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3" />
        </svg>
      </div>
      <h2 className="text-xl font-bold text-gray-900 mb-2">No Unit Assigned</h2>
      <p className="text-gray-600">
        Your account hasn't been linked to a unit yet. Please contact your strata manager
        and make sure the email on file matches: <strong>{data?.owner?.email}</strong>
      </p>
    </div>
  );

  const { unit, pending_fees, recent_payments, open_requests, summary } = data;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome back, {data.owner.first_name}!
        </h1>
        <p className="text-gray-500">
          Unit {unit.unit_number} · {unit.building_name} · {unit.building_address}
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <SummaryCard
          label="Outstanding Balance"
          value={`$${summary.outstanding_amount.toFixed(2)}`}
          color={summary.outstanding_amount > 0 ? 'red' : 'green'}
          icon="💳"
          sub={`${pending_fees.length} fee${pending_fees.length !== 1 ? 's' : ''} pending`}
        />
        <SummaryCard
          label="Paid This Year"
          value={`$${summary.total_paid_ytd.toFixed(2)}`}
          color="blue"
          icon="✓"
          sub="Year-to-date payments"
        />
        <SummaryCard
          label="Open Requests"
          value={summary.open_maintenance}
          color={summary.open_maintenance > 0 ? 'yellow' : 'green'}
          icon="🔧"
          sub="Maintenance requests"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Fees */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-900">Outstanding Fees</h2>
            <Link to="/owner/payments" className="text-blue-600 hover:underline text-sm">
              Pay Now →
            </Link>
          </div>
          {pending_fees.length === 0 ? (
            <div className="px-5 py-8 text-center text-gray-400">
              <span className="text-3xl">🎉</span>
              <p className="mt-2">No outstanding fees</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {pending_fees.slice(0, 5).map((fee) => (
                <li key={fee.id} className="flex items-center justify-between px-5 py-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{fee.period_label || fee.description}</p>
                    <p className="text-xs text-gray-500">Due {fee.due_date}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${FEE_STATUS_COLORS[fee.status] || ''}`}>
                      {fee.status}
                    </span>
                    <span className="font-semibold text-gray-900">${fee.amount.toFixed(2)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {pending_fees.length > 0 && (
            <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 rounded-b-xl">
              <Link to="/owner/payments"
                className="block w-full text-center bg-blue-600 text-white text-sm font-medium py-2 rounded-lg hover:bg-blue-700 transition-colors">
                Pay ${summary.outstanding_amount.toFixed(2)} Now
              </Link>
            </div>
          )}
        </div>

        {/* Maintenance Requests */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-900">Maintenance Requests</h2>
            <Link to="/owner/maintenance" className="text-blue-600 hover:underline text-sm">
              View All →
            </Link>
          </div>
          {open_requests.length === 0 ? (
            <div className="px-5 py-8 text-center text-gray-400">
              <span className="text-3xl">✅</span>
              <p className="mt-2">No open requests</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {open_requests.map((req) => (
                <li key={req.id} className="px-5 py-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{req.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5 capitalize">{req.category?.replace('_', ' ')}</p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${STATUS_COLORS[req.status] || 'bg-gray-100 text-gray-600'}`}>
                      {req.status.replace('_', ' ')}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 rounded-b-xl">
            <Link to="/owner/maintenance"
              className="block w-full text-center border border-blue-600 text-blue-600 text-sm font-medium py-2 rounded-lg hover:bg-blue-50 transition-colors">
              Submit New Request
            </Link>
          </div>
        </div>

        {/* Unit Info */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-900">Unit Information</h2>
          </div>
          <div className="px-5 py-4 grid grid-cols-2 gap-3 text-sm">
            {[
              ['Unit Number', unit.unit_number],
              ['Building', unit.building_name],
              ['Floor', unit.floor || '—'],
              ['Bedrooms', unit.bedrooms || '—'],
              ['Bathrooms', unit.bathrooms || '—'],
              ['Sq. Footage', unit.square_footage ? `${unit.square_footage} sqft` : '—'],
              ['Parking Stall', unit.parking_stall || '—'],
              ['Monthly Fee', unit.monthly_fee ? `$${unit.monthly_fee}` : '—'],
            ].map(([k, v]) => (
              <div key={k}>
                <p className="text-gray-500 text-xs">{k}</p>
                <p className="font-medium text-gray-900">{v}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Payments */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-900">Recent Payments</h2>
            <Link to="/owner/payments" className="text-blue-600 hover:underline text-sm">
              History →
            </Link>
          </div>
          {recent_payments.length === 0 ? (
            <div className="px-5 py-8 text-center text-gray-400">
              <p>No payments yet</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {recent_payments.map((p) => (
                <li key={p.id} className="flex items-center justify-between px-5 py-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{p.description || 'Strata fee'}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(p.created_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Paid</span>
                    <span className="font-semibold text-green-700">${p.amount.toFixed(2)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, color, icon, sub }) {
  const colors = {
    red: 'from-red-50 to-red-100 border-red-200',
    green: 'from-green-50 to-green-100 border-green-200',
    blue: 'from-blue-50 to-blue-100 border-blue-200',
    yellow: 'from-yellow-50 to-yellow-100 border-yellow-200',
  };
  const textColors = {
    red: 'text-red-700', green: 'text-green-700', blue: 'text-blue-700', yellow: 'text-yellow-700',
  };
  return (
    <div className={`bg-gradient-to-br ${colors[color]} border rounded-xl p-5`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-medium text-gray-600">{label}</p>
        <span className="text-xl">{icon}</span>
      </div>
      <p className={`text-2xl font-bold ${textColors[color]}`}>{value}</p>
      <p className="text-xs text-gray-500 mt-1">{sub}</p>
    </div>
  );
}
